export type FeatureFlagAssignmentMode = "targeted" | "global_only";

export type FeatureFlagCategory =
  | "toofi"
  | "reader"
  | "student"
  | "admin";

export type FeatureFlagCatalogEntry = {
  id: string;
  label: string;
  description: string;
  category: FeatureFlagCategory;
  categoryLabel: string;
  audienceLabel: string;
  rolloutLabel: string;
  recommendedModeLabel: string;
  assignmentMode: FeatureFlagAssignmentMode;
  knownOrder: number;
  parentId?: string;
  defaultEnabled?: boolean;
};

type FeatureFlagListItem = {
  id: string;
  label: string;
};

export const FEATURE_FLAG_CATALOG: Record<string, FeatureFlagCatalogEntry> = {
  toofi: {
    id: "toofi",
    label: "Toofi AI",
    description: "Master switch for Toofi across public pages, student surfaces, and related upgrade entry points.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Site-wide",
    rolloutLabel: "Global visibility",
    recommendedModeLabel: "Global on while Toofi is part of the product",
    assignmentMode: "global_only",
    knownOrder: 1,
    defaultEnabled: true,
  },
  toofi_plans: {
    id: "toofi_plans",
    label: "Toofi Plans",
    description: "Show Toofi pricing, upgrade routes, and student plan entry points across the product.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Site-wide",
    rolloutLabel: "Upgrade surfaces",
    recommendedModeLabel: "Keep aligned with Toofi visibility",
    assignmentMode: "global_only",
    knownOrder: 5,
    parentId: "toofi",
    defaultEnabled: true,
  },
  ai_explain: {
    id: "ai_explain",
    label: "AI Explain",
    description: "Enable Toofi and the AI explanation panel in reader-linked student surfaces.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Students",
    rolloutLabel: "Student surface",
    recommendedModeLabel: "Global on with plan entitlements",
    assignmentMode: "targeted",
    knownOrder: 10,
    parentId: "toofi",
    defaultEnabled: true,
  },
  toofi_today_banner: {
    id: "toofi_today_banner",
    label: "Today with Toofi",
    description: "Show the dashboard nudge banner for Today with Toofi on student surfaces.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Students",
    rolloutLabel: "Student surface",
    recommendedModeLabel: "Targeted beta first",
    assignmentMode: "targeted",
    knownOrder: 20,
    parentId: "toofi",
  },
  toofi_heartbeat: {
    id: "toofi_heartbeat",
    label: "Toofi Heartbeat",
    description: "Run scheduled Toofi nudge generation through the backend heartbeat job.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Backend",
    rolloutLabel: "Backend job",
    recommendedModeLabel: "Global only",
    assignmentMode: "global_only",
    knownOrder: 30,
    parentId: "toofi",
  },
  toofi_artifact_shelf: {
    id: "toofi_artifact_shelf",
    label: "Toofi Artifact Shelf",
    description: "Enable the shared local-first artifact shelf across workspace and reader Toofi shells.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Students",
    rolloutLabel: "Toofi shell",
    recommendedModeLabel: "Targeted beta first",
    assignmentMode: "targeted",
    knownOrder: 35,
    parentId: "toofi",
  },
  toofi_memory_mode: {
    id: "toofi_memory_mode",
    label: "Toofi Temporary Chat",
    description: "Enable thread-level temporary chat and memory mode controls in Toofi shells.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Students",
    rolloutLabel: "Toofi shell",
    recommendedModeLabel: "Targeted beta first",
    assignmentMode: "targeted",
    knownOrder: 36,
    parentId: "toofi",
  },
  toofi_nudge_actions: {
    id: "toofi_nudge_actions",
    label: "Toofi Nudge Actions",
    description: "Enable why-this, snooze, and 24-hour pause actions for Toofi nudges in the header inbox.",
    category: "toofi",
    categoryLabel: "Toofi",
    audienceLabel: "Students",
    rolloutLabel: "Header notifications",
    recommendedModeLabel: "Targeted beta first",
    assignmentMode: "targeted",
    knownOrder: 37,
    parentId: "toofi",
  },
  study_time: {
    id: "study_time",
    label: "Study Time",
    description: "Enable study time tracking, study pacing cards, and related student workflows.",
    category: "student",
    categoryLabel: "Student",
    audienceLabel: "Students",
    rolloutLabel: "Student surface",
    recommendedModeLabel: "Targeted beta or global rollout",
    assignmentMode: "targeted",
    knownOrder: 40,
  },
  offers: {
    id: "offers",
    label: "Pass Offers",
    description: "Show pass and bundle offer discovery on library profile pages.",
    category: "student",
    categoryLabel: "Student",
    audienceLabel: "Students",
    rolloutLabel: "Student surface",
    recommendedModeLabel: "Targeted beta first",
    assignmentMode: "targeted",
    knownOrder: 50,
  },
  reader_translation: {
    id: "reader_translation",
    label: "Reader Translation",
    description: "Enable translated reading inside the PDF reader when translated content exists.",
    category: "reader",
    categoryLabel: "Reader",
    audienceLabel: "Students",
    rolloutLabel: "Reader surface",
    recommendedModeLabel: "Targeted beta or global rollout",
    assignmentMode: "targeted",
    knownOrder: 60,
  },
  admin_inbox: {
    id: "admin_inbox",
    label: "Admin Inbox",
    description: "Enable direct admin-to-student inbox messaging surfaces.",
    category: "admin",
    categoryLabel: "Admin",
    audienceLabel: "Students",
    rolloutLabel: "Messaging surface",
    recommendedModeLabel: "Global on",
    assignmentMode: "targeted",
    knownOrder: 70,
  },
};

export const getFeatureFlagCatalogEntry = (flagId: string): FeatureFlagCatalogEntry | null =>
  FEATURE_FLAG_CATALOG[flagId] ?? null;

export const getFeatureFlagChildren = (flagId: string) =>
  Object.values(FEATURE_FLAG_CATALOG)
    .filter((entry) => entry.parentId === flagId)
    .sort((left, right) => left.knownOrder - right.knownOrder);

export const getFeatureFlagRoots = () =>
  Object.values(FEATURE_FLAG_CATALOG)
    .filter((entry) => !entry.parentId)
    .sort((left, right) => left.knownOrder - right.knownOrder);

export const getFeatureFlagDescendants = (flagId: string): FeatureFlagCatalogEntry[] => {
  const directChildren = getFeatureFlagChildren(flagId);
  return directChildren.flatMap((child) => [child, ...getFeatureFlagDescendants(child.id)]);
};

export const getFeatureFlagDefaultEnabled = (flagId: string) =>
  Boolean(getFeatureFlagCatalogEntry(flagId)?.defaultEnabled);

export const resolveFeatureFlagMap = (rawFlags: Record<string, boolean>) => {
  const resolved: Record<string, boolean> = {};
  const visiting = new Set<string>();
  const allFlagIds = new Set([...Object.keys(FEATURE_FLAG_CATALOG), ...Object.keys(rawFlags)]);

  const resolveFlag = (flagId: string): boolean => {
    if (flagId in resolved) return resolved[flagId];
    if (visiting.has(flagId)) {
      return Boolean(rawFlags[flagId] ?? getFeatureFlagDefaultEnabled(flagId));
    }

    visiting.add(flagId);

    const entry = getFeatureFlagCatalogEntry(flagId);
    const ownValue = Boolean(rawFlags[flagId] ?? entry?.defaultEnabled);
    const resolvedValue = entry?.parentId ? ownValue && resolveFlag(entry.parentId) : ownValue;

    visiting.delete(flagId);
    resolved[flagId] = resolvedValue;
    return resolvedValue;
  };

  allFlagIds.forEach((flagId) => {
    resolveFlag(flagId);
  });

  return resolved;
};

export const getDefaultResolvedFeatureFlag = (flagId: string) =>
  resolveFeatureFlagMap({})[flagId] ?? false;

export const sortFeatureFlagList = <T extends FeatureFlagListItem>(flags: T[]) =>
  [...flags].sort((left, right) => {
    const leftMeta = getFeatureFlagCatalogEntry(left.id);
    const rightMeta = getFeatureFlagCatalogEntry(right.id);
    const leftOrder = leftMeta?.knownOrder ?? 5000;
    const rightOrder = rightMeta?.knownOrder ?? 5000;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return left.label.localeCompare(right.label);
  });
